import Dexie from 'dexie'
import type { RealMachinesDatabase } from '@/db/database'
import { getDatabase } from '@/db/database'
import { DatabaseOperationError, executeDatabaseOperation } from '@/db/errors'
import { createPhotoInputSchema, photoPreviewSchema, photoSchema } from '@/db/schemas'
import { createUuid } from '@/db/uuid'
import type { CreatePhotoInput, CreatePhotoPreviewInput, Photo, PhotoPreview } from '@/db/types'
import { requireRecord, writeAuditLog } from './helpers'

export class PhotoRepository {
  constructor(private readonly database: RealMachinesDatabase = getDatabase()) {}

  private async requireEditableInspection(inspectionId: string) {
    const inspection = requireRecord(await this.database.inspections.get(inspectionId), 'Inspeção')
    if (inspection.status !== 'draft') {
      throw new DatabaseOperationError(
        'invalid_state',
        'Evidências fotográficas só podem ser alteradas enquanto o relatório é rascunho.',
        'Este relatório está fechado e suas fotos não podem mais ser alteradas.',
      )
    }
    return inspection
  }

  async nextInspectionOrder(inspectionId: string): Promise<number> {
    return executeDatabaseOperation('calcular ordem da foto', async () => {
      const last = await this.database.photos
        .where('[inspectionId+order]')
        .between([inspectionId, Dexie.minKey], [inspectionId, Dexie.maxKey])
        .last()
      return (last?.order ?? -1) + 1
    })
  }

  async create(input: CreatePhotoInput, previewInput?: CreatePhotoPreviewInput): Promise<Photo> {
    return executeDatabaseOperation('salvar foto', async () => {
      const data = createPhotoInputSchema.parse(input)
      return this.database.transaction(
        'rw',
        [
          this.database.photos,
          this.database.photoPreviews,
          this.database.machines,
          this.database.inspections,
          this.database.inspectionResults,
          this.database.auditLogs,
        ],
        async () => {
          if (data.machineId) {
            requireRecord(await this.database.machines.get(data.machineId), 'Máquina')
          }
          if (data.inspectionId) {
            await this.requireEditableInspection(data.inspectionId)
          }
          if (data.resultId) {
            const result = requireRecord(
              await this.database.inspectionResults.get(data.resultId),
              'Resultado',
            )
            if (result.inspectionId !== data.inspectionId) {
              throw new DatabaseOperationError(
                'relationship',
                'O resultado não pertence à inspeção da foto.',
              )
            }
          }
          if (previewInput && !data.inspectionId) {
            throw new DatabaseOperationError(
              'relationship',
              'Preview de evidência exige uma inspeção.',
            )
          }

          const now = new Date().toISOString()
          const photo = photoSchema.parse({
            ...data,
            id: createUuid(),
            createdAt: now,
            updatedAt: now,
          })
          await this.database.photos.add(photo)

          if (previewInput && photo.inspectionId) {
            const preview = photoPreviewSchema.parse({
              id: photo.id,
              photoId: photo.id,
              inspectionId: photo.inspectionId,
              ...(photo.resultId ? { resultId: photo.resultId } : {}),
              category: photo.category,
              thumbnailBlob: previewInput.blob,
              thumbnailMimeType: previewInput.mimeType,
              thumbnailWidth: previewInput.width,
              thumbnailHeight: previewInput.height,
              thumbnailSize: previewInput.size,
              fullMimeType: photo.mimeType,
              fullWidth: photo.width,
              fullHeight: photo.height,
              fullSize: photo.size,
              caption: photo.caption,
              order: photo.order,
              createdAt: now,
              updatedAt: now,
            })
            await this.database.photoPreviews.add(preview)
          }

          if (photo.machineId) {
            await this.database.machines.update(photo.machineId, {
              photoId: photo.id,
              updatedAt: now,
            })
          }
          await writeAuditLog(
            this.database,
            'photo',
            photo.id,
            'create',
            { category: photo.category, resultId: photo.resultId ?? null, size: photo.size },
            now,
          )
          return photo
        },
      )
    })
  }

  async getById(id: string): Promise<Photo | undefined> {
    return executeDatabaseOperation('consultar foto', async () => {
      const photo = await this.database.photos.get(id)
      return photo ? photoSchema.parse(photo) : undefined
    })
  }

  async listByInspection(inspectionId: string, limit = 100): Promise<Photo[]> {
    return executeDatabaseOperation('listar fotos da inspeção', async () => {
      const records = await this.database.photos
        .where('inspectionId')
        .equals(inspectionId)
        .limit(Math.min(Math.max(limit, 1), 500))
        .sortBy('order')
      return records.map((record) => photoSchema.parse(record))
    })
  }

  async listPreviewsByInspection(inspectionId: string, limit = 200): Promise<PhotoPreview[]> {
    return executeDatabaseOperation('listar miniaturas da inspeção', async () => {
      const records = await this.database.photoPreviews
        .where('[inspectionId+order]')
        .between([inspectionId, Dexie.minKey], [inspectionId, Dexie.maxKey])
        .limit(Math.min(Math.max(limit, 1), 500))
        .toArray()
      return records.map((record) => photoPreviewSchema.parse(record))
    })
  }

  async update(id: string, input: { caption?: string; order?: number }): Promise<Photo> {
    return executeDatabaseOperation('editar foto', async () =>
      this.database.transaction(
        'rw',
        [
          this.database.photos,
          this.database.photoPreviews,
          this.database.inspections,
          this.database.auditLogs,
        ],
        async () => {
          if (input.caption === undefined && input.order === undefined) {
            throw new DatabaseOperationError('validation', 'Nenhuma alteração foi informada.')
          }
          const existing = photoSchema.parse(
            requireRecord(await this.database.photos.get(id), 'Foto'),
          )
          if (existing.inspectionId) await this.requireEditableInspection(existing.inspectionId)
          const now = new Date().toISOString()
          const photo = photoSchema.parse({
            ...existing,
            ...(input.caption !== undefined ? { caption: input.caption.trim() } : {}),
            ...(input.order !== undefined ? { order: input.order } : {}),
            updatedAt: now,
          })
          await this.database.photos.put(photo)
          const existingPreview = await this.database.photoPreviews.get(id)
          if (existingPreview) {
            await this.database.photoPreviews.put(
              photoPreviewSchema.parse({
                ...existingPreview,
                caption: photo.caption,
                order: photo.order,
                updatedAt: now,
              }),
            )
          }
          await writeAuditLog(
            this.database,
            'photo',
            id,
            'update',
            { fields: Object.keys(input).sort() },
            now,
          )
          return photo
        },
      ),
    )
  }

  async reorderGeneral(inspectionId: string, orderedIds: readonly string[]): Promise<void> {
    return executeDatabaseOperation('reordenar fotos gerais', async () =>
      this.database.transaction(
        'rw',
        [
          this.database.photos,
          this.database.photoPreviews,
          this.database.inspections,
          this.database.auditLogs,
        ],
        async () => {
          await this.requireEditableInspection(inspectionId)
          const general = (
            await this.database.photoPreviews.where('inspectionId').equals(inspectionId).toArray()
          )
            .filter((preview) => preview.category === 'general')
            .sort((left, right) => left.order - right.order)
          const expectedIds = general.map((preview) => preview.id).sort()
          const receivedIds = [...orderedIds].sort()
          if (
            expectedIds.length !== receivedIds.length ||
            expectedIds.some((id, index) => id !== receivedIds[index])
          ) {
            throw new DatabaseOperationError(
              'validation',
              'A reordenação precisa conter exatamente as fotos gerais desta inspeção.',
            )
          }
          const orderSlots = general.map((preview) => preview.order).sort((a, b) => a - b)
          const now = new Date().toISOString()
          for (const [index, id] of orderedIds.entries()) {
            const order = orderSlots[index]
            if (order === undefined) continue
            await this.database.photos.update(id, { order, updatedAt: now })
            const preview = general.find((candidate) => candidate.id === id)
            if (!preview) continue
            await this.database.photoPreviews.put(
              photoPreviewSchema.parse({ ...preview, order, updatedAt: now }),
            )
            await writeAuditLog(this.database, 'photo', id, 'update', { fields: ['order'] }, now)
          }
        },
      ),
    )
  }

  async delete(id: string): Promise<void> {
    return executeDatabaseOperation('apagar foto', async () =>
      this.database.transaction(
        'rw',
        [
          this.database.photos,
          this.database.photoPreviews,
          this.database.machines,
          this.database.inspections,
          this.database.auditLogs,
        ],
        async () => {
          const photo = photoSchema.parse(requireRecord(await this.database.photos.get(id), 'Foto'))
          if (photo.inspectionId) await this.requireEditableInspection(photo.inspectionId)
          const now = new Date().toISOString()
          await this.database.photos.delete(id)
          await this.database.photoPreviews.delete(id)
          if (photo.machineId) {
            const machine = await this.database.machines.get(photo.machineId)
            if (machine?.photoId === id) {
              await this.database.machines.update(photo.machineId, {
                photoId: undefined,
                updatedAt: now,
              })
            }
          }
          await writeAuditLog(this.database, 'photo', id, 'delete', {}, now)
        },
      ),
    )
  }
}
